import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  getMonth,
  startOfWeek,
  subDays,
} from 'date-fns'
import { useMemo } from 'react'

export interface ContributionDay {
  date: string
  count: number
}

interface ProgressCalendarProps {
  data: ContributionDay[]
  colors?: string[]
}

const defaultColors = [
  'var(--progress-calendar-empty)',
  '#9be9a8',
  '#40c463',
  '#30a14e',
  '#216e39',
]

// A column is a vertical slice of 7 cells (Sun–Sat).
// null means an empty/invisible cell.
interface Column {
  id: string // stable key for React rendering
  cells: ({ day: Date; count: number } | null)[]
  isMonthStart: boolean // true = this column starts a new month (add gap before it)
  monthLabel: string | null // label to show below this column
}

export function ProgressCalendar({
  data,
  colors = defaultColors,
}: ProgressCalendarProps) {
  // Keep the Date identities stable across renders so the `stats`/`columns`
  // useMemos below don't rerun their full year-long loops on every unrelated
  // re-render of the problems page. Keying off `dayStamp` (a primitive that only
  // changes when the calendar day does) preserves that stability within a day
  // while still rolling the trailing-year window forward across midnight — e.g.
  // for a tab left open overnight — which a bare `[]` dep would freeze.
  const dayStamp = format(new Date(), 'yyyy-MM-dd')
  // biome-ignore lint/correctness/useExhaustiveDependencies: dayStamp is intentionally the sole dep — the callback reads `new Date()` fresh, and dayStamp (a primitive that only changes at a day boundary) is what re-triggers the memo so the trailing-year window rolls forward across midnight.
  const { today, startDate } = useMemo(() => {
    const t = new Date()
    return { today: t, startDate: subDays(t, 364) }
  }, [dayStamp])

  const countMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const item of data) {
      map.set(item.date, item.count)
    }
    return map
  }, [data])

  // Compute stats
  const stats = useMemo(() => {
    let total = 0
    let activeDays = 0
    let maxStreak = 0
    let currentStreak = 0

    for (let i = 0; i <= 364; i++) {
      const day = addDays(startDate, i)
      const key = format(day, 'yyyy-MM-dd')
      const count = countMap.get(key) ?? 0
      total += count
      if (count > 0) {
        activeDays++
        currentStreak++
        if (currentStreak > maxStreak) maxStreak = currentStreak
      } else {
        currentStreak = 0
      }
    }

    return { total, activeDays, maxStreak }
  }, [countMap, startDate])

  const getColor = (count: number) => {
    if (count === 0) return colors[0]
    if (count === 1) return colors[1]
    if (count === 2) return colors[2]
    if (count === 3) return colors[3]
    return colors[4] || colors[colors.length - 1]
  }

  // Build columns, splitting weeks at month boundaries
  const columns = useMemo(() => {
    const result: Column[] = []
    let currentWeekStart = startOfWeek(startDate, { weekStartsOn: 0 })
    let prevMonth = -1
    let isFirst = true

    while (currentWeekStart <= today) {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 0 })
      const weekDays = eachDayOfInterval({
        start: currentWeekStart,
        end: weekEnd,
      })

      // Check if this week spans a month boundary
      const firstMonth = getMonth(weekDays[0])
      const lastMonth = getMonth(weekDays[6])
      const spansMonths = firstMonth !== lastMonth

      if (spansMonths) {
        // Split into two columns: old month days, then new month days
        const oldMonthCells: Column['cells'] = []
        const newMonthCells: Column['cells'] = []

        for (let i = 0; i < 7; i++) {
          const day = weekDays[i]
          const isInRange = day >= startDate && day <= today
          const dayMonth = getMonth(day)

          if (dayMonth === firstMonth) {
            oldMonthCells.push(
              isInRange
                ? { day, count: countMap.get(format(day, 'yyyy-MM-dd')) ?? 0 }
                : null,
            )
            newMonthCells.push(null)
          } else {
            oldMonthCells.push(null)
            newMonthCells.push(
              isInRange
                ? { day, count: countMap.get(format(day, 'yyyy-MM-dd')) ?? 0 }
                : null,
            )
          }
        }

        // Old month column — only include if it has visible days
        const hasOldMonthDays = oldMonthCells.some((c) => c !== null)
        if (hasOldMonthDays) {
          const isOldMonthStart =
            isFirst || (prevMonth !== -1 && firstMonth !== prevMonth)
          result.push({
            id: `${format(currentWeekStart, 'yyyy-MM-dd')}-a`,
            cells: oldMonthCells,
            isMonthStart: isOldMonthStart,
            monthLabel: isFirst ? format(weekDays[0], 'MMM') : null,
          })
        }

        // New month column — only include if it has visible days
        const hasNewMonthDays = newMonthCells.some((c) => c !== null)
        if (hasNewMonthDays) {
          result.push({
            id: `${format(currentWeekStart, 'yyyy-MM-dd')}-b`,
            cells: newMonthCells,
            isMonthStart: true,
            monthLabel: format(
              weekDays.find((d) => getMonth(d) === lastMonth)!,
              'MMM',
            ),
          })
        }

        prevMonth = hasNewMonthDays ? lastMonth : firstMonth
      } else {
        // Whole week is in one month
        const cells: Column['cells'] = weekDays.map((day) => {
          const isInRange = day >= startDate && day <= today
          if (!isInRange) return null
          return { day, count: countMap.get(format(day, 'yyyy-MM-dd')) ?? 0 }
        })

        const isMonthStart =
          isFirst || (prevMonth !== -1 && firstMonth !== prevMonth)

        result.push({
          id: format(currentWeekStart, 'yyyy-MM-dd'),
          cells,
          isMonthStart,
          monthLabel:
            isFirst || isMonthStart ? format(weekDays[0], 'MMM') : null,
        })

        prevMonth = firstMonth
      }

      isFirst = false
      currentWeekStart = addDays(currentWeekStart, 7)
    }

    return result
  }, [countMap, startDate, today])

  return (
    <div className="progress-calendar border-border rounded-lg border p-4">
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-baseline gap-y-1">
        <div>
          <span className="text-foreground text-lg font-bold">
            {stats.total}
          </span>{' '}
          <span className="text-muted-foreground text-sm">
            submissions in the past one year
          </span>
        </div>
        <div className="text-muted-foreground ml-auto flex gap-4 text-sm">
          <span>
            Total active days:{' '}
            <span className="text-foreground">{stats.activeDays}</span>
          </span>
          <span>
            Max streak:{' '}
            <span className="text-foreground">{stats.maxStreak}</span>
          </span>
        </div>
      </div>

      {/* Grid */}
      <div className="w-full">
        <div className="flex w-full flex-col">
          {/* Day columns */}
          <div className="flex w-full items-end gap-[2px]">
            {columns.map((col, colIdx) => (
              <div
                key={col.id}
                className="flex min-w-0 flex-1 flex-col gap-[2px]"
                style={{
                  marginLeft: col.isMonthStart && colIdx > 0 ? 6 : 0,
                }}
              >
                {col.cells.map((cell, cellIdx) =>
                  cell ? (
                    <div
                      key={format(cell.day, 'yyyy-MM-dd')}
                      className="aspect-square w-full rounded-[3px]"
                      style={{ backgroundColor: getColor(cell.count) }}
                      title={`${format(cell.day, 'PPP')}: ${cell.count} submissions`}
                    />
                  ) : (
                    <div
                      key={`empty-${col.id}-${cellIdx}`}
                      className="aspect-square w-full"
                    />
                  ),
                )}
              </div>
            ))}
          </div>

          {/* Month labels below */}
          <div className="mt-1.5 flex h-4 w-full gap-[2px]">
            {columns.map((col, colIdx) => (
              <div
                key={col.id}
                className="relative min-w-0 flex-1"
                style={{
                  marginLeft: col.isMonthStart && colIdx > 0 ? 6 : 0,
                }}
              >
                {col.monthLabel && (
                  <span className="text-muted-foreground absolute left-0 top-0 whitespace-nowrap text-xs">
                    {col.monthLabel}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
